import type { GridColDef } from '@mui/x-data-grid';
import type { GridColumnGroupingModel } from '@mui/x-data-grid-pro';
import type { CommitmentVsUsageMonth, CommitmentVsUsageReportItem } from '@vy/types';
import { VyTableCell } from '../../../../../components/new-design/table/vy-table/VyTableCell';
import {
  formatContractEnd,
  formatMonthHeader,
  formatNumber,
  formatPercent,
  formatReportDate,
  formatResetCell,
  formatResetCycle,
} from '../commitment-vs-usage-format-helpers';

function buildStaticColumns(): GridColDef<CommitmentVsUsageReportItem>[] {
  return [
    {
      field: 'customer.name',
      headerName: 'Customer',
      minWidth: 180,
      flex: 1,
      headerAlign: 'left',
      align: 'left',
      valueGetter: (_value, row) => row.customer.name,
      renderCell: ({ row }) => <VyTableCell value={row.customer.name} />,
    },
    {
      field: 'customer.externalId',
      headerName: 'ERP ID',
      minWidth: 120,
      flex: 1,
      headerAlign: 'left',
      align: 'left',
      valueGetter: (_value, row) => row.customer.externalId ?? '',
      renderCell: ({ row }) => <VyTableCell value={row.customer.externalId ?? undefined} />,
    },
    {
      field: 'product.variantName',
      headerName: 'Product / Meter',
      minWidth: 160,
      flex: 1,
      headerAlign: 'left',
      align: 'left',
      valueGetter: (_value, row) => row.product.variantName,
      renderCell: ({ row }) => <VyTableCell value={row.product.variantName} />,
    },
    {
      field: 'contract.name',
      headerName: 'Contract Name',
      minWidth: 160,
      flex: 1,
      headerAlign: 'left',
      align: 'left',
      valueGetter: (_value, row) => row.contract.name,
      renderCell: ({ row }) => <VyTableCell value={row.contract.name} />,
    },
    {
      field: 'contract.status',
      headerName: 'Status',
      minWidth: 120,
      flex: 1,
      headerAlign: 'left',
      align: 'left',
      valueGetter: (_value, row) => row.contract.status,
      renderCell: ({ row }) => <VyTableCell value={row.contract.status} />,
    },
    {
      field: 'contract.startDate',
      headerName: 'Contract Start',
      minWidth: 130,
      flex: 1,
      headerAlign: 'left',
      align: 'left',
      valueGetter: (_value, row) => row.contract.startDate,
      renderCell: ({ row }) => <VyTableCell value={formatReportDate(row.contract.startDate)} />,
    },
    {
      field: 'contract.endDate',
      headerName: 'Contract End',
      minWidth: 130,
      flex: 1,
      headerAlign: 'left',
      align: 'left',
      valueGetter: (_value, row) => row.contract.endDate ?? '',
      renderCell: ({ row }) => <VyTableCell value={formatContractEnd(row.contract.endDate)} />,
    },
    {
      field: 'contract.daysToEnd',
      headerName: 'Days to End',
      minWidth: 110,
      flex: 1,
      type: 'number',
      headerAlign: 'left',
      align: 'left',
      valueGetter: (_value, row) => row.contract.daysToEnd ?? '',
      renderCell: ({ row }) => <VyTableCell value={formatNumber(row.contract.daysToEnd)} />,
    },
    {
      field: 'commitment.resetIntervalMonths',
      headerName: 'Reset Cycle',
      minWidth: 120,
      flex: 1,
      headerAlign: 'left',
      align: 'left',
      valueGetter: (_value, row) => row.commitment.resetIntervalMonths ?? '',
      renderCell: ({ row }) => <VyTableCell value={formatResetCycle(row.commitment.resetIntervalMonths)} />,
    },
    {
      field: 'commitment.units',
      headerName: 'Qty Commitment',
      minWidth: 130,
      flex: 1,
      type: 'number',
      headerAlign: 'left',
      align: 'left',
      valueGetter: (_value, row) => row.commitment.units ?? '',
      renderCell: ({ row }) => <VyTableCell value={formatNumber(row.commitment.units)} />,
    },
    {
      field: 'currentCycle.consumed',
      headerName: 'Consumed (Qty)',
      minWidth: 130,
      flex: 1,
      type: 'number',
      headerAlign: 'left',
      align: 'left',
      valueGetter: (_value, row) => row.currentCycle.consumed ?? '',
      renderCell: ({ row }) => <VyTableCell value={formatNumber(row.currentCycle.consumed)} />,
    },
    {
      field: 'currentCycle.pctUsage',
      headerName: '% Usage',
      minWidth: 100,
      flex: 1,
      headerAlign: 'left',
      align: 'left',
      valueGetter: (_value, row) => row.currentCycle.pctUsage ?? '',
      renderCell: ({ row }) => <VyTableCell value={formatPercent(row.currentCycle.pctUsage)} />,
    },
    {
      field: 'currentCycle.pacing',
      headerName: 'Pacing %',
      minWidth: 100,
      flex: 1,
      headerAlign: 'left',
      align: 'left',
      valueGetter: (_value, row) => row.currentCycle.pacing ?? '',
      renderCell: ({ row }) => <VyTableCell value={formatPercent(row.currentCycle.pacing)} />,
    },
    {
      field: 'currentCycle.projectedEoP',
      headerName: 'Projected EoP (Qty)',
      minWidth: 150,
      flex: 1,
      type: 'number',
      headerAlign: 'left',
      align: 'left',
      valueGetter: (_value, row) => row.currentCycle.projectedEoP ?? '',
      renderCell: ({ row }) => <VyTableCell value={formatNumber(row.currentCycle.projectedEoP)} />,
    },
    {
      field: 'currentCycle.shortfall',
      headerName: 'Shortfall (Qty)',
      minWidth: 130,
      flex: 1,
      type: 'number',
      headerAlign: 'left',
      align: 'left',
      valueGetter: (_value, row) => row.currentCycle.shortfall ?? '',
      renderCell: ({ row }) => <VyTableCell value={formatNumber(row.currentCycle.shortfall)} />,
    },
    {
      field: 'currentCycle.overage',
      headerName: 'Overage (Qty)',
      minWidth: 130,
      flex: 1,
      type: 'number',
      headerAlign: 'left',
      align: 'left',
      valueGetter: (_value, row) => row.currentCycle.overage ?? '',
      renderCell: ({ row }) => <VyTableCell value={formatNumber(row.currentCycle.overage)} />,
    },
  ];
}

function buildMonthColumns(index: number, monthStartTime: Date): GridColDef<CommitmentVsUsageReportItem>[] {
  const monthTime = monthStartTime.getTime();
  const getEntry = (row: CommitmentVsUsageReportItem) =>
    row.monthlyEntries.find((e) => e.startTime.getTime() === monthTime);

  return [
    {
      field: `monthly.${index}.qtyUsed`,
      headerName: 'Qty Used',
      minWidth: 100,
      flex: 1,
      type: 'number',
      headerAlign: 'left',
      align: 'left',
      valueGetter: (_value, row) => getEntry(row)?.qtyUsed ?? null,
      renderCell: ({ row }) => <VyTableCell value={formatNumber(getEntry(row)?.qtyUsed)} />,
    },
    {
      field: `monthly.${index}.cumulativeQty`,
      headerName: 'Cumul. Qty',
      minWidth: 100,
      flex: 1,
      type: 'number',
      headerAlign: 'left',
      align: 'left',
      valueGetter: (_value, row) => getEntry(row)?.cumulativeQty ?? null,
      renderCell: ({ row }) => <VyTableCell value={formatNumber(getEntry(row)?.cumulativeQty)} />,
    },
    {
      field: `monthly.${index}.cumulativePct`,
      headerName: 'Cumul. %',
      minWidth: 100,
      flex: 1,
      headerAlign: 'left',
      align: 'left',
      valueGetter: (_value, row) => getEntry(row)?.cumulativePct ?? null,
      renderCell: ({ row }) => <VyTableCell value={formatPercent(getEntry(row)?.cumulativePct)} />,
    },
    {
      field: `monthly.${index}.reset`,
      headerName: 'Reset',
      minWidth: 80,
      flex: 1,
      headerAlign: 'left',
      align: 'left',
      valueGetter: (_value, row) => getEntry(row)?.isResetMonth ?? null,
      renderCell: ({ row }) => (
        <VyTableCell value={formatResetCell(getEntry(row)?.isResetMonth)} />
      ),
    },
  ];
}

export function buildCommitmentVsUsageColumns(
  months: CommitmentVsUsageMonth[],
): GridColDef<CommitmentVsUsageReportItem>[] {
  return [...buildStaticColumns(), ...months.flatMap((month, index) => buildMonthColumns(index, month.startTime))];
}

export function buildCommitmentVsUsageColumnGroups(months: CommitmentVsUsageMonth[]): GridColumnGroupingModel {
  return months.map((month, index) => ({
    groupId: `month-${index}`,
    headerName: formatMonthHeader(month.startTime),
    headerAlign: 'left' as const,
    children: buildMonthColumns(index, month.startTime).map(({ field }) => ({ field })),
  }));
}
